import type { GameModel } from 'yage/game/GameModel';
import { Transform } from 'yage/schemas/entity/Transform';
import { AirportAgent, AirportPassenger, AirportStaff } from '../components/index.js';
import { tier } from '../domain/airports.js';
import { boardingWorkPoint } from '../domain/queues.js';
import { onPad } from '../domain/config.js';
import { boardingWindowOpen, earlyBoardingWindowOpen, ensureTimetable, BOARDING_SHORTLY_FRAMES, BOARDING_DESK_FRAMES, DESK_DELAY_GRACE } from '../domain/timetable.js';
import { planes } from './operations.js';
import { worksDesk } from './crew.js';
import { ids, notice, state } from './world.js';

/** Desk work changes with the flight schedule, after passenger questions are finished. */
export function automaticBoarding(g: GameModel, answeredQuestion: boolean | ReadonlySet<number> = false): boolean {
    const aircraft = planes(g);
    aircraft.forEach(p => ensureTimetable(p, g.frame));
    const waiting = ids(g, AirportPassenger).map(id => g.getTypedUnsafe(AirportPassenger, id)).filter(p => !p.arriving && p.stage === 6 && [0, 1, 2, 6, 7].includes(p.state));
    let opened = false;
    for (const plane of aircraft) {
        const pad = boardingWorkPoint(plane.gate, tier(state(g)).code);
        const question = (typeof answeredQuestion === "boolean" ? answeredQuestion : answeredQuestion.has(plane.gate)) || ids(g, AirportPassenger).some(id => { const p = g.getTypedUnsafe(AirportPassenger, id); return p.state === 7 && p.gate === plane.gate; });
        const captain = ids(g, AirportAgent).some(id => onPad('work', g.getTypedUnsafe(Transform, id), pad));
        const helper = ids(g, AirportStaff).some(id => { const worker = g.getTypedUnsafe(AirportStaff, id); return worksDesk(worker) && worker.stage === 6 && onPad('work', g.getTypedUnsafe(Transform, id), pad); });
        if (plane.boardingCalled && plane.boarded >= plane.seats) { plane.unattendedSince = -1; continue; }
        const ready = waiting.some(p => p.state === 6 || p.gate === plane.gate);
        const early = earlyBoardingWindowOpen(plane,g.frame) && ready && (captain || helper);
        if (!boardingWindowOpen(plane, g.frame) && !early) continue;
        if (plane.boardingAnnouncedFrame < 0) {
            plane.boardingAnnouncedFrame = g.frame;
            notice(g, `Gate ${String.fromCharCode(65 + plane.gate)} · Boarding shortly. Finish questions and work at the desk.`, 2);
        }
        const prelude = g.frame - plane.boardingAnnouncedFrame < BOARDING_SHORTLY_FRAMES;
        const deskDue = plane.boardingCalled || g.frame >= plane.scheduledDepartureFrame;
        if (!captain && !helper && !prelude && deskDue) {
            if (plane.unattendedSince < 0) plane.unattendedSince = g.frame;
            if (g.frame - plane.unattendedSince === DESK_DELAY_GRACE) notice(g, `Gate ${String.fromCharCode(65 + plane.gate)} delayed · the boarding desk has been unattended.`, 3);
        } else plane.unattendedSince = -1;
        if (!captain && !helper || question || g.frame < plane.aircraftReadyFrame) { plane.boardingDeskFrames = 0; continue; }
        plane.boardingDeskFrames = Math.min(BOARDING_DESK_FRAMES, plane.boardingDeskFrames + 1);
        if (!early && (prelude || plane.boardingDeskFrames < BOARDING_DESK_FRAMES) || plane.boardingCalled) continue;
        if (ids(g, AirportPassenger).some(id => { const q = g.getTypedUnsafe(AirportPassenger, id); return q.arriving && q.gate === plane.gate && q.walkStep < 2; })) continue;
        plane.boardingCalled = true; plane.boardingStartedFrame = g.frame; opened = true;
    }
    if (opened) notice(g, 'Boarding is open. Passengers are joining the line.', 2);
    return opened;
}
